import { Controller } from "@hotwired/stimulus";
import { isEmpty } from "lodash";
import { useStore } from "stimulus-store";

import { deploymentStatusStore } from "../stores/deployment_status_store";
import { bundleStore } from "../stores/device_types/bundle_store";
import { regionStore } from "../stores/locales/region_store";
import { buildingStore } from "../stores/locales/building_store";
import { areaStore } from "../stores/locales/area_store";
import { roomStore } from "../stores/locales/room_store";
import { getDeploymentStatuses } from "shared/selects/deployment_statuses";
import { sendSuccessNotification, sendFailureNotification } from "shared/toast_notifications";
import { getCsrfToken } from "shared/form_helpers";

/**
 * Add Bundle Form Controller
 *
 * STORE INTEGRATION:
 * - Uses stimulus-store convention with on<StoreName>Update methods
 * - Reactive updates: onBundleStoreUpdate, onRegionStoreUpdate, etc.
 * - Store values accessed via this.<storeName>Value pattern
 *
 * TURBO DRIVE NOTE: All page-specific stores (regionStore, buildingStore,
 * areaStore, roomStore, bundleStore, deploymentStatusStore) are module-level
 * singletons that persist across Turbo navigations. The locale stores
 * (region/building/area/room) are cleared by their own select controllers'
 * disconnect() methods when leaving the previous page (see
 * region_select_controller.js). The bundleStore and deploymentStatusStore may
 * also carry stale values from a previous page — consider clearing them in
 * connect() if issues arise.
 */

// Connects to data-controller="add-bundle-form"
export default class extends Controller {
    static targets = [
        "bundleSelect",
        "bundleDetails",
        "bundleName",
        "bundleIcon",
        "deviceTypesList",
        "partsList",
        "bundleNameInput",
        "regionSelect",
        "buildingSelect",
        "areaSelect",
        "roomSelect",
        "deviceConfiguration",
        "deviceConfigurationForms",
        "floorPlanSection",
        "floorPlanContainer",
        "floorPlanInstructions",
        "coordinateX",
        "coordinateY"
    ]
    static stores = [
        deploymentStatusStore,
        bundleStore,
        regionStore,
        buildingStore,
        areaStore,
        roomStore
    ]

    connect() {
        useStore(this);
    }

    initialize() {
        this.initializeForm();
    }

    disconnect() {
        // Clean up periodic state synchronization
        // Requirements 1.10, 1.11, 3.1, 3.2
        if (this.stateSyncInterval) {
            clearInterval(this.stateSyncInterval);
            this.stateSyncInterval = null;
        }
    }

    initializeForm() {
        // Initialize form state
        this.deviceConfigurations = {};

        // Disable location fields initially
        this.disableLocationFields();

        // Hide preview sections initially
        this.hideBundleDetails();
        this.hideDeviceConfiguration();
        this.hideFloorPlanSection();

        // Disable deploy button initially
        this.disableDeployButton();
    }

    // Stimulus-store reactive update methods following the on<StoreName>Update convention
    // These methods are automatically called when store values change
    // For accessing current store values, use this.<storeName>Value pattern
    // Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 2.1, 2.2

    onDeploymentStatusStoreUpdate(newValue) {
        // Watch for deployment status store changes for reactive validation
        this.updateDeployButtonState();
    }

    onRegionStoreUpdate(newValue) {
        this.updateDeployButtonState();
    }

    onBuildingStoreUpdate(newValue) {
        this.updateDeployButtonState();
    }

    onAreaStoreUpdate(newValue) {
        // The floor-plan controller will handle the display automatically
        // We just need to show/hide the floor plan section and update button state
        if (!isEmpty(newValue)) {
            this.showFloorPlanSection();
        } else {
            this.hideFloorPlanSection();
        }

        this.updateDeployButtonState();
    }

    onRoomStoreUpdate(newValue) {
        this.updateDeployButtonState();
    }

    onBundleStoreUpdate(newValue) {
        // Handle bundle selection changes
        const bundle = newValue;

        if (!isEmpty(bundle)) {
            // Bundle selected - show bundle details and device configuration
            this.showBundleDetails(bundle);
            this.showDeviceConfiguration(bundle);
            this.enableLocationFields();

            // Reset device configurations for new bundle
            this.deviceConfigurations = {};

            // Show floor plan section if area is selected
            if (!isEmpty(this.areaStoreValue)) {
                this.showFloorPlanSection();
            }
        } else {
            // Bundle cleared - hide sections and disable location fields
            this.deviceConfigurations = {};

            this.hideBundleDetails();
            this.hideDeviceConfiguration();
            this.hideFloorPlanSection();
            this.disableLocationFields();
        }

        this.updateDeployButtonState();
    }

    // Bundle details display methods
    async showBundleDetails(bundleData) {
        if (this.hasBundleDetailsTarget) {
            // Update bundle information
            if (this.hasBundleNameTarget) {
                this.bundleNameTarget.textContent = bundleData.name || bundleData.text || '';
            }

            if (this.hasBundleIconTarget) {
                this.bundleIconTarget.innerHTML = this.formatBundleIcon(bundleData.icon);
            }

            // Update device types list (async)
            if (this.hasDeviceTypesListTarget) {
                this.deviceTypesListTarget.innerHTML = '<li class="text-muted"><em>Loading device types...</em></li>';
                try {
                    const deviceTypesHtml = await this.buildDeviceTypesList(bundleData.models || []);
                    this.deviceTypesListTarget.innerHTML = deviceTypesHtml;
                } catch (error) {
                    console.error('Failed to load device types:', error);
                    this.deviceTypesListTarget.innerHTML = '<li class="text-muted"><em>Failed to load device types</em></li>';
                }
            }

            // Update parts list (async)
            if (this.hasPartsListTarget) {
                this.partsListTarget.innerHTML = '<li class="text-muted"><em>Loading parts...</em></li>';
                try {
                    const partsHtml = await this.buildPartsList(bundleData.parts || []);
                    this.partsListTarget.innerHTML = partsHtml;
                } catch (error) {
                    console.error('Failed to load parts:', error);
                    this.partsListTarget.innerHTML = '<li class="text-muted"><em>Failed to load parts</em></li>';
                }
            }

            // Show the bundle details section
            this.bundleDetailsTarget.style.display = 'block';
        }
    }

    hideBundleDetails() {
        if (this.hasBundleDetailsTarget) {
            this.bundleDetailsTarget.style.display = 'none';
        }
    }

    async buildDeviceTypesList(models) {
        if (!models || models.length === 0) {
            return '<li class="text-muted"><em>No device types defined</em></li>';
        }

        try {
            // Fetch model metadata using the device_types/device_model/describe API
            const modelIds = models.map(model => typeof model === 'string' ? model : model.id).filter(Boolean);

            if (modelIds.length === 0) {
                return '<li class="text-muted"><em>No valid model IDs found</em></li>';
            }

            // Build URL with multiple id parameters
            const params = new URLSearchParams();
            modelIds.forEach(id => params.append('id', id));

            const response = await fetch(`/api/device_types/device_model/describe?id=${encodeURIComponent(modelIds.join(','))}`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            // Handle API error responses (Requirement 8.1)
            if (data.status === 'error') {
                throw new Error(data.message || 'Failed to load device type definitions');
            }

            const deviceTypeDefinitions = data.device_type_definitions || [];

            if (deviceTypeDefinitions.length === 0) {
                return '<li class="text-muted"><em>No device type definitions found</em></li>';
            }

            return deviceTypeDefinitions.map(definition => {
                const name = definition.name || `Device Type ${definition.id || 'Unknown'}`;

                return `<li class="mb-1">
                    <i class="fas fa-desktop me-2 text-primary"></i>
                    <span class="fw-medium">${name}</span>
                </li>`;
            }).join('');
        } catch (error) {
            console.error('Failed to fetch device type definitions:', error);

            // Show user-friendly error message for device type loading issues (Requirement 8.1)
            let errorMessage = 'Failed to load device type information';
            if (error.message.includes('HTTP 404')) {
                errorMessage = 'Some device types in this bundle could not be found. The bundle definition may be outdated.';
            } else if (error.message.includes('HTTP 403')) {
                errorMessage = 'You do not have permission to access device type information for this bundle.';
            } else if (error.message.includes('network') || error.message.includes('fetch')) {
                errorMessage = 'Network error: Unable to load device type details.';
            }

            this.showWarningMessage(errorMessage);

            // Fallback to showing model IDs
            return models.map(model => {
                const id = typeof model === 'string' ? model : model.id || 'Unknown';
                return `<li class="mb-1">
                    <i class="fas fa-desktop me-2 text-primary"></i>
                    <span class="fw-medium">Model ${id}</span>
                </li>`;
            }).join('');
        }
    }

    async buildPartsList(parts) {
        if (!parts || parts.length === 0) {
            return '<li class="text-muted"><em>No parts defined</em></li>';
        }

        try {
            // Fetch part metadata using the device_types/part/describe API
            const partIds = parts.map(part => typeof part === 'string' ? part : part.id).filter(Boolean);

            if (partIds.length === 0) {
                return '<li class="text-muted"><em>No valid part IDs found</em></li>';
            }

            // Build URL with multiple id parameters
            const params = new URLSearchParams();
            partIds.forEach(id => params.append('id', id));

            const response = await fetch(`/api/device_types/part/describe?id=${encodeURIComponent(partIds.join(','))}`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            // Handle API error responses (Requirement 8.1)
            if (data.status === 'error') {
                throw new Error(data.message || 'Failed to load part definitions');
            }

            const deviceTypeDefinitions = data.parts || [];

            if (deviceTypeDefinitions.length === 0) {
                return '<li class="text-muted"><em>No part definitions found</em></li>';
            }

            return deviceTypeDefinitions.map(definition => {
                const name = definition.name || `Part ${definition.id || 'Unknown'}`;

                return `<li class="mb-1">
                    <i class="fas fa-cog me-2 text-secondary"></i>
                    <span class="fw-medium">${name}</span>
                </li>`;
            }).join('');
        } catch (error) {
            console.error('Failed to fetch part definitions:', error);

            // Show user-friendly error message for part loading issues (Requirement 8.1)
            let errorMessage = 'Failed to load part information';
            if (error.message.includes('HTTP 404')) {
                errorMessage = 'Some parts in this bundle could not be found. The bundle definition may be outdated.';
            } else if (error.message.includes('HTTP 403')) {
                errorMessage = 'You do not have permission to access part information for this bundle.';
            } else if (error.message.includes('network') || error.message.includes('fetch')) {
                errorMessage = 'Network error: Unable to load part details.';
            }

            this.showWarningMessage(errorMessage);

            // Fallback to showing part IDs
            return parts.map(part => {
                const id = typeof part === 'string' ? part : part.id || 'Unknown';
                return `<li class="mb-1">
                    <i class="fas fa-cog me-2 text-secondary"></i>
                    <span class="fw-medium">Part ${id}</span>
                </li>`;
            }).join('');
        }
    }

    // Helper method to fetch deployment statuses using shared function
    async getDeploymentStatuses() {
        try {
            // Use the shared getDeploymentStatusesForSelect function
            const statuses = await getDeploymentStatuses();

            // Convert to the format expected by TomSelect (add value property)
            return statuses.map(status => ({
                id: status.id,
                name: status.name,
                value: status.id
            }));
        } catch (error) {
            console.error('Failed to fetch deployment statuses:', error);
            throw new Error('Failed to load deployment statuses');
        }
    }

    // Device configuration methods
    async showDeviceConfiguration(bundleData) {
        if (!this.hasDeviceConfigurationTarget || !this.hasDeviceConfigurationFormsTarget) return;

        // Only show configuration for devices (models), not parts
        const models = bundleData.models || [];

        if (models.length === 0) {
            this.hideDeviceConfiguration();
            return;
        }

        // Fetch model details to get actual model names
        const modelsWithNames = await this.fetchModelDetails(models);

        // Fetch deployment statuses from API
        const deploymentStatuses = await this.getDeploymentStatuses();

        // Generate device configuration forms with actual model names
        this.deviceConfigurationFormsTarget.innerHTML = this.buildDeviceConfigurationForms(modelsWithNames, deploymentStatuses);

        // Initialize device configurations object
        this.deviceConfigurations = {};
        modelsWithNames.forEach((model, index) => {
            const deviceId = model.id || `device_${index}`;
            this.deviceConfigurations[deviceId] = {
                ip_address: '',
                mac_address: '',
                dns_name: '',
                deployment_status: ''
            };
        });

        // Deployment status selects are now handled by the deployment-statuses controller
        // via data-controller="selects--deployment-statuses--deployment-status-select" on each select element

        // Set up device configuration event listeners using the new approach
        this.setupDeviceConfigurationEventListeners();

        // Show the device configuration section
        this.deviceConfigurationTarget.style.display = 'block';

        // Update deploy button state after device configuration is set up
        // Use a small delay to ensure DOM is fully updated
        setTimeout(() => {
            this.updateDeployButtonState();
        }, 100);
    }

    hideDeviceConfiguration() {
        if (this.hasDeviceConfigurationTarget) {
            this.deviceConfigurationTarget.style.display = 'none';
        }
    }

    async fetchModelDetails(models) {
        try {
            // Extract model IDs from the models array
            const modelIds = models.map(model => typeof model === 'string' ? model : model.id).filter(Boolean);

            if (modelIds.length === 0) {
                return models; // Return original models if no IDs found
            }

            // Fetch model metadata using the device_types/device_model/describe API
            const response = await fetch(`/api/device_types/device_model/describe?id=${encodeURIComponent(modelIds.join(','))}`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            // Handle API error responses
            if (data.status === 'error') {
                throw new Error(data.message || 'Failed to load model details');
            }

            const deviceTypeDefinitions = data.device_type_definitions || [];

            // Map the original models to include the fetched names
            return models.map((model, index) => {
                const modelId = typeof model === 'string' ? model : model.id;
                const definition = deviceTypeDefinitions.find(def => def.id === modelId);

                return {
                    ...model,
                    id: modelId,
                    device_type: definition?.device_type || '',
                    manufacturer: definition?.manufacturer || '',
                    name: definition?.name || `Device ${index + 1}`,
                    text: definition?.name || `Device ${index + 1}`
                };
            });
        } catch (error) {
            console.error('Failed to fetch model details:', error);

            // Fallback to original models with generic names
            return models.map((model, index) => ({
                ...model,
                id: typeof model === 'string' ? model : model.id,
                name: `Device ${index + 1}`,
                text: `Device ${index + 1}`
            }));
        }
    }

    buildDeviceConfigurationForms(models, deploymentStatuses) {
        if (!models || models.length === 0) {
            return '<p class="text-muted">No devices to configure</p>';
        }

        return models.map((model, index) => {
            const deviceId = model.id || `device_${index}`;
            const deviceName = model.text || model.name || `Device ${index + 1}`;
            const statusOptions = this.buildDeploymentStatusOptions(deploymentStatuses);

            // Generate hidden input fields for device type information
            const hiddenTypeFields = `
                <input type="hidden" 
                       name="devices[${index}][type][device_type]" 
                       value="${model.device_type || ''}"
                       data-device-id="${deviceId}">
                <input type="hidden" 
                       name="devices[${index}][type][manufacturer]" 
                       value="${model.manufacturer || ''}"
                       data-device-id="${deviceId}">
                <input type="hidden" 
                       name="devices[${index}][type][model]" 
                       value="${model.id || ''}"
                       data-device-id="${deviceId}">
            `;

            return `
                <div class="device-config-card mb-4 p-3 border rounded" data-device-id="${deviceId}">
                    ${hiddenTypeFields}
                    <h6 class="mb-3">
                        <i class="fas fa-desktop me-2 text-primary"></i>
                        ${deviceName}
                    </h6>
                    <div class="row">
                        <div class="col-md-6 mb-3">
                            <label for="deployment-status-${deviceId}" class="form-label">Deployment Status</label>
                            <select id="deployment-status-${deviceId}" 
                                    class="form-select device-config-input deployment-status-select" 
                                    data-device-id="${deviceId}"
                                    data-property="deployment_status"
                                    data-controller="selects--deployment-statuses--deployment-status-select"
                                ${statusOptions}
                            </select>
                        </div>
                        <div class="col-md-4 mb-3">
                            <label class="form-label">IP Address</label>
                            <input type="text" 
                                   class="form-control device-config-input" 
                                   data-device-id="${deviceId}"
                                   data-property="ip_address"
                                   placeholder="192.168.1.100" 
                                   pattern="^(?:[0-9]{1,3}\\.){3}[0-9]{1,3}$">
                            <div class="form-text">Format: 192.168.1.100</div>
                        </div>
                        <div class="col-md-4 mb-3">
                            <label class="form-label">MAC Address</label>
                            <input type="text" 
                                   class="form-control device-config-input" 
                                   data-device-id="${deviceId}"
                                   data-property="mac_address"
                                   placeholder="00:1B:44:11:3A:B7" 
                                   pattern="^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$">
                            <div class="form-text">Format: 00:1B:44:11:3A:B7</div>
                        </div>
                        <div class="col-md-4 mb-3">
                            <label class="form-label">DNS Name</label>
                            <input type="text" 
                                   class="form-control device-config-input" 
                                   data-device-id="${deviceId}"
                                   data-property="dns_name"
                                   placeholder="device.example.com" 
                                   pattern="^[a-zA-Z0-9]([a-zA-Z0-9\\-]{0,61}[a-zA-Z0-9])?(\\.[a-zA-Z0-9]([a-zA-Z0-9\\-]{0,61}[a-zA-Z0-9])?)*$">
                            <div class="form-text">Format: device.example.com</div>
                        </div>
                    </div>
                </div>
            `;
        }).join('');
    }

    buildDeploymentStatusOptions(deploymentStatuses) {
        if (!deploymentStatuses || deploymentStatuses.length === 0) {
            return `
                <option value="">Select deployment status</option>
                <option value="planned">Planned</option>
                <option value="active">Active</option>
                <option value="maintenance">Maintenance</option>
                <option value="decommissioned">Decommissioned</option>
            `;
        }

        let options = '<option value="">Select deployment status</option>';
        options += deploymentStatuses.map(status => {
            return `<option value="${status.id}">${status.name}</option>`;
        }).join('');

        return options;
    }

    setupDeviceConfigurationEventListeners() {
        // Set up event listeners for device configuration inputs using the new approach
        // Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 2.1, 2.2
        const configInputs = this.element.querySelectorAll('.device-config-input');

        configInputs.forEach(input => {
            // Add comprehensive null/undefined checks for form elements before accessing properties
            // Requirements 1.10, 1.11, 3.1, 3.2
            if (!input || !input.dataset) {
                console.warn('Invalid input element found in device configuration:', input);
                return;
            }

            // Add event listeners for all form field types that trigger validation
            input.addEventListener('input', (e) => {
                this.handleDeviceConfigurationChange(e.target);
            });

            input.addEventListener('change', (e) => {
                this.handleDeviceConfigurationChange(e.target);
            });

            // Blur events for validation feedback
            input.addEventListener('blur', (e) => {
                this.handleDeviceConfigurationChange(e.target);
            });

            // Add focus events for real-time state synchronization
            // Requirements 1.10, 1.11, 3.1, 3.2
            input.addEventListener('focus', (e) => {
                this.syncDeviceConfigurationState();
            });
        });

        // Set up periodic state synchronization for real-time updates
        // Requirements 1.10, 1.11, 3.1, 3.2
        this.setupPeriodicStateSynchronization();
    }

    handleDeviceConfigurationChange(input) {

        // Handle device configuration changes and update the store
        // Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 2.1, 2.2
        this.updateDeviceConfiguration(input);

        // Add real-time state updates that sync deviceConfigurations object with actual form values
        // Requirements 1.10, 1.11, 3.1, 3.2
        this.syncDeviceConfigurationState();

        // Device configurations are managed locally in this.deviceConfigurations

        // Ensure validation triggers reactively when device configurations change
        // Requirements 1.8, 1.9, 3.3, 3.4
        this.updateDeployButtonState();
    }

    setupPeriodicStateSynchronization() {
        // Set up periodic state synchronization for real-time updates
        // Requirements 1.10, 1.11, 3.1, 3.2
        if (this.stateSyncInterval) {
            clearInterval(this.stateSyncInterval);
        }

        // Sync state every 500ms when form is active
        this.stateSyncInterval = setInterval(() => {
            if (this.isFormActive()) {
                this.syncDeviceConfigurationState();
            }
        }, 500);
    }

    isFormActive() {
        // Check if the form is currently active (has focus or recent interaction)
        // Requirements 1.10, 1.11, 3.1, 3.2
        const activeElement = document.activeElement;
        if (!activeElement) return false;

        // Check if active element is within this controller's element
        return this.element.contains(activeElement) &&
            activeElement.classList.contains('device-config-input');
    }

    syncDeviceConfigurationState() {
        // Add real-time state updates that sync deviceConfigurations object with actual form values
        // Requirements 1.10, 1.11, 3.1, 3.2
        try {
            const configInputs = this.element.querySelectorAll('.device-config-input');
            let stateChanged = false;

            configInputs.forEach(input => {
                // Add comprehensive null/undefined checks for form elements before accessing properties
                if (!input || !input.dataset) {
                    return;
                }

                const deviceId = input.dataset.deviceId;
                const property = input.dataset.property;

                if (!deviceId || !property) {
                    return;
                }

                // Initialize device configuration if it doesn't exist
                if (!this.deviceConfigurations[deviceId]) {
                    this.deviceConfigurations[deviceId] = {};
                    stateChanged = true;
                }

                // Get current value from form element
                let currentValue = '';
                try {
                    if (input.tomselect && typeof input.tomselect.getValue === 'function') {
                        currentValue = input.tomselect.getValue() || '';
                    } else if (input.tagName && input.tagName.toLowerCase() === 'select') {
                        currentValue = input.value || '';
                    } else {
                        currentValue = (input.value || '').trim();
                    }
                } catch (error) {
                    console.warn('Error getting value from input element:', error);
                    currentValue = input.value || '';
                }

                // Check if value has changed
                const storedValue = this.deviceConfigurations[deviceId][property];
                if (storedValue !== currentValue) {
                    this.deviceConfigurations[deviceId][property] = currentValue;
                    stateChanged = true;

                    // Log state change for debugging
                    if (console && console.debug) {
                        console.debug('Device configuration state synchronized:', {
                            deviceId,
                            property,
                            oldValue: storedValue,
                            newValue: currentValue,
                            timestamp: new Date().toISOString()
                        });
                    }
                }
            });

            // Device configurations are managed locally in this.deviceConfigurations

        } catch (error) {
            console.error('Error in syncDeviceConfigurationState:', error);
            // Continue gracefully without throwing
        }
    }

    handleTomSelectChange(select, value) {
        // Handle TomSelect changes and update the store
        // Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 2.1, 2.2
        this.updateDeviceConfigurationFromTomSelect(select, value);

        // Device configurations are managed locally in this.deviceConfigurations
    }

    setupTomSelectEventHandlers(select) {
        // Add TomSelect-specific event listeners (change, item_add, item_remove)
        // Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 2.1, 2.2
        try {
            const tomselect = select.tomselect;

            if (!tomselect) {
                console.warn('TomSelect instance not found for element:', select);
                return;
            }

            // Add item_add event listener
            tomselect.on('item_add', (value, item) => {
                this.handleTomSelectChange(select, value);
            });

            // Add item_remove event listener  
            tomselect.on('item_remove', (value) => {
                const currentValue = tomselect.getValue();
                this.handleTomSelectChange(select, currentValue);
            });

        } catch (error) {
            console.error('Error setting up TomSelect event handlers:', error);
        }
    }

    updateDeviceConfigurationFromTomSelect(select, value) {
        // Update device configuration specifically for TomSelect elements
        const deviceId = select.dataset.deviceId;
        const property = select.dataset.property;

        if (deviceId && property) {
            if (!this.deviceConfigurations[deviceId]) {
                this.deviceConfigurations[deviceId] = {};
            }
            this.deviceConfigurations[deviceId][property] = value || '';
        }
    }

    updateDeviceConfiguration(input) {
        // Add comprehensive null/undefined checks for form elements before accessing properties
        // Requirements 1.10, 1.11, 3.1, 3.2
        try {
            // Enhanced safety checks for input element
            if (!input) {
                console.warn('Null or undefined input element passed to updateDeviceConfiguration');
                return;
            }

            if (!input.dataset) {
                console.warn('Input element missing dataset property:', input);
                return;
            }

            // Check if element is still in the DOM
            if (!input.isConnected) {
                console.warn('Input element is no longer connected to DOM:', input);
                return;
            }

            const deviceId = input.dataset.deviceId;
            const property = input.dataset.property;

            // Enhanced safety checks for required data attributes
            if (!deviceId || deviceId.trim() === '') {
                console.warn('Missing or empty deviceId data attribute:', {deviceId, element: input});
                return;
            }

            if (!property || property.trim() === '') {
                console.warn('Missing or empty property data attribute:', {property, element: input});
                return;
            }

            // Skip TomSelect elements - they handle their own updates via onChange handlers
            if (input.tomselect) {
                return;
            }

            // Get value safely with enhanced error handling
            let value = '';
            try {
                if (input.value !== undefined && input.value !== null) {
                    value = String(input.value).trim();
                } else {
                    console.warn('Input element has undefined or null value:', input);
                    value = '';
                }
            } catch (valueError) {
                console.warn('Error accessing input value:', valueError);
                value = '';
            }

            // Initialize device configuration object if it doesn't exist
            if (!this.deviceConfigurations) {
                this.deviceConfigurations = {};
            }

            if (!this.deviceConfigurations[deviceId]) {
                this.deviceConfigurations[deviceId] = {};
            }

            // Store previous value for change detection
            const previousValue = this.deviceConfigurations[deviceId][property];
            this.deviceConfigurations[deviceId][property] = value;

            // Add real-time state updates that sync deviceConfigurations object with actual form values
            // Requirements 1.10, 1.11, 3.1, 3.2
            if (console && console.debug && previousValue !== value) {
                console.debug('Device configuration updated:', {
                    deviceId,
                    property,
                    previousValue,
                    newValue: value,
                    timestamp: new Date().toISOString(),
                    allConfigurations: this.deviceConfigurations
                });
            }

        } catch (error) {
            console.error('Error updating device configuration:', error);
            // Graceful degradation - continue without throwing
        }
    }

    updateDeployButtonState() {
        // Calculate form validity directly from current state
        const isValid = this.calculateFormValidity();

        // Update button state based on validation
        if (isValid) {
            this.enableDeployButton();
        } else {
            this.disableDeployButton();
        }

        // Add visual feedback that clearly indicates when button state changes
        this.updateButtonVisualFeedback(isValid);
    }

    calculateFormValidity() {
        // Check if bundle select has a selection
        if (!this.isBundleSelected()) {
            return false;
        }

        // Check if location selects have selections
        if (!this.isLocationComplete()) {
            return false;
        }

        // Check if required device configuration inputs have values
        if (!this.areDeviceConfigurationsComplete()) {
            return false;
        }

        return true;
    }

    isBundleSelected() {
        // Check if bundle select element has a value
        const bundleSelect = document.getElementById('bundle-select');
        if (!bundleSelect) return false;

        // Check if the select has a value (either native select or TomSelect)
        if (bundleSelect.tomselect) {
            const value = bundleSelect.tomselect.getValue();
            return value && value.length > 0;
        }

        return bundleSelect.value && bundleSelect.value.trim() !== '';
    }

    isLocationComplete() {
        // Check if location select elements have values
        const locationSelects = [
            'region-select',
            'building-select',
            'area-select',
            'room-select'
        ];

        return locationSelects.every(selectId => {
            const select = document.getElementById(selectId);
            if (!select) return false;

            // Check if the select has a value (either native select or TomSelect)
            if (select.tomselect) {
                const value = select.tomselect.getValue();
                return value && value.length > 0;
            }

            return select.value && select.value.trim() !== '';
        });
    }

    areDeviceConfigurationsComplete() {
        // Bundle name is optional - no need to check it

        // Check other required device configuration inputs
        const otherRequiredInputs = this.element.querySelectorAll('.device-config-input[required]:not([data-property="name"])');

        return Array.from(otherRequiredInputs).every(input => {
            if (!input) {
                return false;
            }

            // For select elements (including TomSelect)
            if (input.tagName.toLowerCase() === 'select') {
                if (input.tomselect) {
                    const value = input.tomselect.getValue();
                    return value && value.length > 0;
                }
                return input.value && input.value.trim() !== '';
            }

            // For regular input elements
            return input.value && input.value.trim() !== '';
        });
    }

    updateButtonVisualFeedback(isValid) {
        const deployButton = document.getElementById('deploy-bundle');
        if (!deployButton) return;

        // Add visual debugging indicators that show current form validation state
        // Requirements 2.3, 2.4, 3.5
        const existingIndicator = deployButton.parentNode.querySelector('.validation-indicator');
        if (existingIndicator) {
            existingIndicator.remove();
        }

        // Create visual indicator for validation state
        const indicator = document.createElement('small');
        indicator.className = 'validation-indicator d-block mt-1';
        indicator.style.fontSize = '0.75rem';

        if (isValid) {
            indicator.className += ' text-success';
            indicator.innerHTML = '<i class="fas fa-check-circle me-1"></i>Form is ready for deployment';
        } else {
            indicator.className += ' text-muted';
            indicator.innerHTML = '<i class="fas fa-info-circle me-1"></i>Complete all required fields to enable deployment';
        }

        deployButton.parentNode.appendChild(indicator);
    }

    getDeviceConfigurations() {
        // Add real-time state updates that sync deviceConfigurations object with actual form values
        // Requirements 1.10, 1.11, 3.1, 3.2
        try {
            // Initialize configurations object if it doesn't exist
            if (!this.deviceConfigurations) {
                this.deviceConfigurations = {};
            }

            // Update configurations from current form values
            const configInputs = this.element.querySelectorAll('.device-config-input');

            configInputs.forEach(input => {
                try {
                    // Add comprehensive null/undefined checks for form elements before accessing properties
                    if (!input) {
                        console.warn('Null input element found in getDeviceConfigurations');
                        return;
                    }

                    if (!input.dataset) {
                        console.warn('Input element missing dataset in getDeviceConfigurations:', input);
                        return;
                    }

                    // Check if element is still connected to DOM
                    if (!input.isConnected) {
                        console.warn('Input element no longer connected to DOM in getDeviceConfigurations:', input);
                        return;
                    }

                    const deviceId = input.dataset.deviceId;
                    const property = input.dataset.property;

                    if (!deviceId || deviceId.trim() === '') {
                        console.warn('Missing or empty deviceId in getDeviceConfigurations:', {
                            deviceId,
                            element: input
                        });
                        return;
                    }

                    if (!property || property.trim() === '') {
                        console.warn('Missing or empty property in getDeviceConfigurations:', {
                            property,
                            element: input
                        });
                        return;
                    }

                    if (!this.deviceConfigurations[deviceId]) {
                        this.deviceConfigurations[deviceId] = {};
                    }

                    // Implement enhanced fallback logic to handle uninitialized TomSelect elements
                    // Requirements 1.10, 1.11, 3.1, 3.2
                    let value = '';

                    try {
                        // For TomSelect elements, get the current value with enhanced error handling
                        if (input.tomselect && typeof input.tomselect.getValue === 'function') {
                            try {
                                const tomSelectValue = input.tomselect.getValue();
                                value = tomSelectValue !== null && tomSelectValue !== undefined ? String(tomSelectValue) : '';
                            } catch (tomSelectError) {
                                console.warn('Error getting TomSelect value, falling back to native select:', tomSelectError);
                                // Enhanced fallback to native select value
                                value = input.value !== null && input.value !== undefined ? String(input.value) : '';
                            }
                        } else if (input.tagName && input.tagName.toLowerCase() === 'select') {
                            // For regular select elements (including those that will become TomSelect)
                            value = input.value !== null && input.value !== undefined ? String(input.value) : '';
                        } else {
                            // For regular input elements with enhanced null checking
                            const inputValue = input.value;
                            if (inputValue !== null && inputValue !== undefined) {
                                value = String(inputValue).trim();
                            } else {
                                value = '';
                            }
                        }
                    } catch (valueError) {
                        console.warn('Error extracting value from input element:', valueError);
                        value = '';
                    }

                    // Store previous value for change detection
                    const previousValue = this.deviceConfigurations[deviceId][property];
                    this.deviceConfigurations[deviceId][property] = value;

                    // Log changes for debugging
                    if (console && console.debug && previousValue !== value) {
                        console.debug('Device configuration synchronized:', {
                            deviceId,
                            property,
                            previousValue,
                            newValue: value,
                            timestamp: new Date().toISOString()
                        });
                    }

                } catch (inputError) {
                    console.error('Error processing input in getDeviceConfigurations:', inputError);
                    // Continue processing other inputs
                }
            });

            // Device configurations are managed locally in this.deviceConfigurations

            // Add comprehensive logging for state synchronization
            if (console && console.debug) {
                console.debug('Device configurations fully synchronized:', {
                    configurations: this.deviceConfigurations,
                    isValid: isValid,
                    inputCount: configInputs.length,
                    timestamp: new Date().toISOString()
                });
            }

            return this.deviceConfigurations;

        } catch (error) {
            console.error('Error in getDeviceConfigurations:', error);
            // Return current configurations even if sync failed, with fallback
            return this.deviceConfigurations || {};
        }
    }

    showFloorPlanSection() {
        if (this.hasFloorPlanSectionTarget) {
            this.floorPlanSectionTarget.style.display = 'block';
        }
    }

    hideFloorPlanSection() {
        if (this.hasFloorPlanSectionTarget) {
            this.floorPlanSectionTarget.style.display = 'none';
        }
    }

    showFloorPlanInstructions() {
        if (this.hasFloorPlanInstructionsTarget) {
            this.floorPlanInstructionsTarget.style.display = 'block';
        }
    }

    hideFloorPlanInstructions() {
        if (this.hasFloorPlanInstructionsTarget) {
            this.floorPlanInstructionsTarget.style.display = 'none';
        }
    }

    getFloorPlanPlacement() {
        // Get coordinates from the hidden fields managed by the floor plan icon manager
        const xCoordinate = this.hasCoordinateXTarget ? this.coordinateXTarget.value : '';
        const yCoordinate = this.hasCoordinateYTarget ? this.coordinateYTarget.value : '';

        if (!xCoordinate || !yCoordinate || xCoordinate.trim() === '' || yCoordinate.trim() === '') {
            return null;
        }

        const x = parseFloat(xCoordinate);
        const y = parseFloat(yCoordinate);

        // Validate that coordinates are valid numbers
        if (isNaN(x) || isNaN(y)) {
            return null;
        }

        return { x, y };
    }

    // Location field management methods
    enableLocationFields() {
        // Enable region select when bundle is selected (Requirement 2.5)
        if (this.hasRegionSelectTarget) {
            this.regionSelectTarget.disabled = false;

            // Also enable the TomSelect instance if it exists
            if (this.regionSelectTarget.tomselect) {
                this.regionSelectTarget.tomselect.enable();
            }
        }
    }

    disableLocationFields() {
        // Disable all location fields when no bundle selected (Requirement 2.5)
        const locationTargets = ['regionSelectTarget', 'buildingSelectTarget', 'areaSelectTarget', 'roomSelectTarget'];

        locationTargets.forEach(targetName => {
            if (this[`has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}`]) {
                const target = this[targetName];
                target.disabled = true;

                // Also disable the TomSelect instance if it exists
                if (target.tomselect) {
                    target.tomselect.disable();
                }
            }
        });
    }

    // Deploy button management with immediate updates
    // Requirements 1.5, 1.6, 2.2, 2.3, 2.4
    enableDeployButton() {
        const deployButton = document.getElementById('deploy-bundle');
        if (deployButton) {
            deployButton.disabled = false;

            // Keep consistent styling with other sidebar buttons (btn-secondary)
            deployButton.classList.remove('btn-primary');
            deployButton.classList.add('btn-secondary');
        }
    }

    disableDeployButton() {
        const deployButton = document.getElementById('deploy-bundle');
        if (deployButton) {
            deployButton.disabled = true;

            // Keep consistent styling with other sidebar buttons (btn-secondary)
            deployButton.classList.remove('btn-primary');
            deployButton.classList.add('btn-secondary');
        }
    }

    // Public methods for accessing current state
    getCurrentBundleData() {
        return this.bundleStoreValue;
    }

    getCurrentLocationData() {
        return {
            region: this.regionStoreValue,
            building: this.buildingStoreValue,
            area: this.areaStoreValue,
            room: this.roomStoreValue
        };
    }

    isFormValid() {
        // Calculate form validity directly
        return this.calculateFormValidity();
    }

    // Action method for deploy button
    async deployBundle(event) {
        if (event) {
            event.preventDefault();
        }

        await this.performDeployment();
    }

    // Deployment methods
    async performDeployment() {
        if (!this.validateDeployment()) {
            return;
        }

        try {
            this.disableFormDuringDeployment();
            this.showDeploymentProgress();

            // Store deployment parameters for retry functionality (Requirement 8.4)
            // Use formatDeploymentPayload to get properly formatted request data with devices array
            this.lastDeploymentParams = this.formatDeploymentPayload();
            const response = await this.submitDeployment(this.lastDeploymentParams);

            if (response.status === "success") {
                // The /api/items create response wraps its payload in a `data` envelope:
                // { status: "success", data: { items: [...], count: N } } (render_success).
                // Read the count/items from response.data, not the top level, otherwise the
                // toast always reports 0 even though the items were created.
                const deployData = response.data || response;
                this.showSuccessMessage(deployData.count ?? deployData.items?.length ?? 0);
                this.resetForm();
                this.clearLastDeploymentParams();
            } else {
                this.handleDeploymentError(response);
            }
        } catch (error) {
            console.error('Deployment error:', error);
            this.handleDeploymentException(error);
        } finally {
            this.enableFormAfterDeployment();
            this.hideDeploymentProgress();
        }
    }

    handleDeploymentError(response) {
        // Handle structured error responses from the server
        const errorCode = response.error_code;
        const errorMessage = response.message || 'Deployment failed';
        const errors = response.errors || [];

        // Determine if retry is appropriate based on error type (Requirement 8.4)
        const retryableErrors = ['SERVICE_UNAVAILABLE', 'DATABASE_ERROR', 'EXTERNAL_SERVICE_ERROR', 'INTERNAL_SERVER_ERROR'];
        const canRetry = retryableErrors.includes(errorCode);

        // Show specific error messages based on error code (Requirements 8.1, 8.2, 8.3)
        let displayMessage = errorMessage;
        if (errors.length > 0) {
            const fieldErrors = errors.map(error => `${error.field}: ${error.message}`).join(', ');
            displayMessage = `${errorMessage}. Details: ${fieldErrors}`;
        }

        // Provide specific guidance based on error type
        switch (errorCode) {
            case 'BUNDLE_NOT_FOUND':
                displayMessage = 'The selected bundle definition could not be found. Please refresh the page and select a different bundle.';
                break;
            case 'LOCATION_HIERARCHY_INVALID':
                displayMessage = 'Invalid location selection. Please select a complete location hierarchy (Region → Building → Area → Room).';
                break;
            case 'INSUFFICIENT_PERMISSIONS':
                displayMessage = 'You do not have permission to deploy bundles in this location. Please contact your organization administrator.';
                break;
            case 'VALIDATION_ERROR':
                displayMessage = 'Validation failed. Please check your device configurations and try again.';
                break;
        }

        this.showErrorMessage(displayMessage, errorCode, canRetry);
    }

    handleDeploymentException(error) {
        // Handle network and other exceptions
        let errorMessage = 'Deployment failed';
        let errorCode = null;
        let canRetry = false;

        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            errorMessage = 'Network error: Unable to connect to the server. Please check your internet connection and try again.';
            canRetry = true;
        } else if (error.message.includes('timeout')) {
            errorMessage = 'Request timeout: The server took too long to respond. Please try again.';
            canRetry = true;
        } else if (error.message.includes('HTTP 500')) {
            errorMessage = 'Server error: The server encountered an internal error. Please try again in a few moments.';
            canRetry = true;
        } else if (error.message.includes('HTTP 503')) {
            errorMessage = 'Service unavailable: The deployment service is temporarily unavailable. Please try again later.';
            canRetry = true;
        } else if (error.message.includes('HTTP 404')) {
            errorMessage = 'Bundle not found: The selected bundle definition could not be found. Please refresh the page and select a different bundle.';
            errorCode = 'BUNDLE_NOT_FOUND';
        } else if (error.message.includes('HTTP 403')) {
            errorMessage = 'Access denied: You do not have permission to deploy bundles in this location. Please contact your organization administrator.';
            errorCode = 'INSUFFICIENT_PERMISSIONS';
        } else if (error.message.includes('HTTP 400')) {
            errorMessage = 'Invalid request: Please check your form data and try again.';
            errorCode = 'INVALID_PARAMETER';
        } else {
            errorMessage = `Deployment failed: ${error.message}`;
            canRetry = true; // Allow retry for unknown errors
        }

        this.showErrorMessage(errorMessage, errorCode, canRetry);
    }

    async retryLastOperation() {
        if (!this.lastDeploymentParams) {
            this.showErrorMessage('No previous operation to retry. Please try deploying again.');
            return;
        }

        // Preserve form data and retry deployment (Requirement 8.4)
        // Regenerate payload to ensure current form state is used
        this.lastDeploymentParams = this.formatDeploymentPayload();
        await this.performDeployment();
    }

    clearLastDeploymentParams() {
        this.lastDeploymentParams = null;
    }

    // Form data preservation methods (Requirement 8.4)
    preserveFormData() {
        // Store current form state for recovery after errors
        return {
            selectedBundle: this.bundleStoreValue,
            selectedLocation: this.getCurrentLocationData(),
            deviceConfigurations: {...this.deviceConfigurations}
        };
    }

    restoreFormData(formData) {
        // Restore form state after error recovery
        if (formData) {
            // Note: Bundle and location state are now managed by stimulus stores, not restored here
            this.deviceConfigurations = formData.deviceConfigurations || {};

            // Update UI to reflect restored state
            this.updateDeployButtonState();
        }
    }

    // Network error detection and retry logic (Requirement 8.4)
    isNetworkError(error) {
        return error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('network') || error.message.includes('Failed to fetch'));
    }

    isRetryableError(errorCode) {
        const retryableErrors = ['SERVICE_UNAVAILABLE', 'DATABASE_ERROR', 'EXTERNAL_SERVICE_ERROR', 'INTERNAL_SERVER_ERROR', 'TIMEOUT_ERROR'];
        return retryableErrors.includes(errorCode);
    }

    validateDeployment() {
        // Enhanced validation with specific error messages (Requirements 8.1, 8.2)

        // Check if bundle is selected
        const bundle = this.bundleStoreValue;
        if (isEmpty(bundle)) {
            this.showErrorMessage('Bundle selection required: Please select a bundle definition from the dropdown to deploy.', 'BUNDLE_NOT_SELECTED');
            return false;
        }

        // Check if location is complete
        if (!this.isLocationComplete()) {
            const missingFields = [];
            if (isEmpty(this.regionStoreValue)) missingFields.push('Region');
            if (isEmpty(this.buildingStoreValue)) missingFields.push('Building');
            if (isEmpty(this.areaStoreValue)) missingFields.push('Area');
            if (isEmpty(this.roomStoreValue)) missingFields.push('Room');

            this.showErrorMessage(`Location hierarchy incomplete: Please select ${missingFields.join(', ')} to complete the location hierarchy.`, 'LOCATION_HIERARCHY_INVALID');
            return false;
        }

        // Validate floor plan placement if floor plan section is visible
        if (this.hasFloorPlanSectionTarget && this.floorPlanSectionTarget.style.display !== 'none') {
            const floorPlanPlacement = this.getFloorPlanPlacement();
            if (!floorPlanPlacement || !floorPlanPlacement.x || !floorPlanPlacement.y) {
                this.showErrorMessage('Floor plan placement required: Please click on the floor plan to place the bundle before deploying. All devices and parts will be assigned to the same coordinates.', 'FLOOR_PLAN_PLACEMENT_REQUIRED');
                return false;
            }
        }

        // Validate device configurations are complete
        if (!this.areDeviceConfigurationsComplete()) {
            this.showErrorMessage('Device configuration incomplete: Please fill in all required device configuration fields before deploying.', 'VALIDATION_ERROR');
            return false;
        }

        // Bundle name is optional - if not provided, the service will generate one

        return true;
    }

    formatDeploymentPayload() {
        const floorPlanPlacement = this.getFloorPlanPlacement();
        const devices = [];

        // Extract device information from hidden form fields and input fields
        const deviceCards = this.element.querySelectorAll('.device-config-card');

        deviceCards.forEach((card, index) => {
            const deviceId = card.dataset.deviceId;

            // Extract type information from hidden form fields
            const deviceTypeField = card.querySelector(`input[name="devices[${index}][type][device_type]"]`);
            const manufacturerField = card.querySelector(`input[name="devices[${index}][type][manufacturer]"]`);
            const modelField = card.querySelector(`input[name="devices[${index}][type][model]"]`);

            // Extract device properties from input fields
            const ipAddressField = card.querySelector(`input[data-device-id="${deviceId}"][data-property="ip_address"]`);
            const macAddressField = card.querySelector(`input[data-device-id="${deviceId}"][data-property="mac_address"]`);
            const dnsNameField = card.querySelector(`input[data-device-id="${deviceId}"][data-property="dns_name"]`);
            const deploymentStatusField = card.querySelector(`select[data-device-id="${deviceId}"][data-property="deployment_status"]`);

            // Get the bundle name from the single input field
            const bundleName = this.hasBundleNameInputTarget ? this.bundleNameInputTarget.value?.trim() || '' : '';

            // Build device object with type structure
            const device = {
                type: {
                    device_type: deviceTypeField?.value || '',
                    manufacturer: manufacturerField?.value || '',
                    model: modelField?.value || ''
                },
                name: bundleName,
                ip_address: ipAddressField?.value?.trim() || '',
                mac_address: macAddressField?.value?.trim() || '',
                dns_name: dnsNameField?.value?.trim() || '',
                deployment_status: deploymentStatusField?.value || ''
            };

            devices.push(device);
        });

        // Return payload with devices array and existing parameters for backward compatibility
        const bundle = this.bundleStoreValue;
        return {
            bundle: bundle.id,
            locale: {
                region: this.regionSelectTarget.value,
                building: this.buildingSelectTarget.value,
                area: this.areaSelectTarget.value,
                room: this.roomSelectTarget.value,
                floorplan_x_coordinate: floorPlanPlacement?.x,
                floorplan_y_coordinate: floorPlanPlacement?.y
            },
            devices: devices
        };
    }

    buildDeploymentParams() {
        const floorPlanPlacement = this.getFloorPlanPlacement();

        const bundle = this.bundleStoreValue;
        return {
            bundle: bundle.id, 
            locale: {
                region: this.regionSelectTarget.value,
                building: this.buildingSelectTarget.value,
                area: this.areaSelectTarget.value,
                room: this.roomSelectTarget.value,
                floorplan_x_coordinate: floorPlanPlacement?.x,
                floorplan_y_coordinate: floorPlanPlacement?.y
            }, 
            device_configurations: this.getDeviceConfigurations()
        };
    }

    async submitDeployment(params) {
        const response = await fetch('/api/items', {
            method: 'POST', headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getCsrfToken(),
                'Accept': 'application/json'
            }, body: JSON.stringify(params)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    }

    disableFormDuringDeployment() {
        this.disableDeployButton();
        this.disableLocationFields();

        // Disable bundle select if it exists
        const bundleSelect = document.getElementById('bundle-select');
        if (bundleSelect && bundleSelect.tomselect) {
            bundleSelect.tomselect.disable();
        }
    }

    enableFormAfterDeployment() {
        // Re-enable form controls based on current state
        const bundle = this.bundleStoreValue;
        if (!isEmpty(bundle)) {
            this.enableLocationFields();
        }

        // Re-enable bundle select
        const bundleSelect = document.getElementById('bundle-select');
        if (bundleSelect && bundleSelect.tomselect) {
            bundleSelect.tomselect.enable();
        }

        // Deploy button will be re-enabled based on form validation
        if (this.isFormValid()) {
            this.enableDeployButton();
        }
    }

    showDeploymentProgress() {
        const deployButton = document.getElementById('deploy-bundle');
        if (deployButton) {
            const originalText = deployButton.textContent;
            deployButton.innerHTML = '<i class="fas fa-spinner fa-spin me-2"></i>Deploying...';
            deployButton.setAttribute('data-original-text', originalText);
        }
    }

    hideDeploymentProgress() {
        const deployButton = document.getElementById('deploy-bundle');
        if (deployButton) {
            const originalText = deployButton.getAttribute('data-original-text');
            if (originalText) {
                deployButton.textContent = originalText;
                deployButton.removeAttribute('data-original-text');
            }
        }
    }

    showSuccessMessage(itemCount) {
        sendSuccessNotification(`Successfully deployed bundle! Created ${itemCount} items.`);
    }

    showErrorMessage(message, errorCode = null, canRetry = false) {
        let fullMessage = message;
        if (errorCode) {
            fullMessage += ` (Error Code: ${errorCode})`;
        }
        
        sendFailureNotification(fullMessage);
        
        // For retryable errors, we could add a retry mechanism here if needed
        // For now, we'll just show the error message
    }

    showWarningMessage(message) {
        // Use failure notification for warnings since the toast library only has success/failure
        sendFailureNotification(message);
    }

    resetForm() {
        // Clear bundle selection
        const bundleSelect = document.getElementById('bundle-select');
        if (bundleSelect && bundleSelect.tomselect) {
            bundleSelect.tomselect.clear(true);
        }

        // Clear location selections
        const locationSelects = ['region-select', 'building-select', 'area-select', 'room-select'];
        locationSelects.forEach(selectId => {
            const select = document.getElementById(selectId);
            if (select && select.tomselect) {
                select.tomselect.clear(true);
            }
        });

        // Clear device configuration forms
        if (this.hasDeviceConfigurationFormsTarget) {
            this.deviceConfigurationFormsTarget.innerHTML = '';
        }

        // Clear coordinate fields managed by the floor plan icon manager
        if (this.hasCoordinateXTarget) {
            this.coordinateXTarget.value = '';
        }
        if (this.hasCoordinateYTarget) {
            this.coordinateYTarget.value = '';
        }

        // Reset form state
        this.initializeForm();
    }

    // Bundle icon helper method
    formatBundleIcon(iconData) {
        // Handle empty or null icon data
        if (!iconData || iconData.trim() === '') {
            return '<i class="fas fa-cube"></i>';  // default icon
        }

        // Check if iconData is already formatted HTML
        if (iconData.includes('<') && iconData.includes('>')) {
            return iconData;  // already formatted HTML
        }

        // Assume it's CSS class names - wrap in <i> tag
        return `<i class="${iconData}"></i>`;
    }
}
